import { createContext, useCallback, useContext, useEffect, useState } from 'react'

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN as string
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string
const REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI as string

const STORAGE_ACCESS_TOKEN = 'auth_access_token'
const STORAGE_SUB = 'auth_sub'
const STORAGE_EMAIL = 'auth_email'
const STORAGE_RETURN_TO = 'auth_return_to'
const STORAGE_CODE_VERIFIER = 'auth_code_verifier'

interface AuthContextValue {
  isAuthenticated: boolean
  sub: string | null
  email: string | null
  accessToken: string | null
  loading: boolean
  login: (returnTo?: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  sub: null,
  email: null,
  accessToken: null,
  loading: true,
  login: () => {},
  logout: () => {}
})

function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(() => sessionStorage.getItem(STORAGE_ACCESS_TOKEN))
  const [sub, setSub] = useState<string | null>(() => sessionStorage.getItem(STORAGE_SUB))
  const [email, setEmail] = useState<string | null>(() => sessionStorage.getItem(STORAGE_EMAIL))
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (accessToken) {
      sessionStorage.setItem(STORAGE_ACCESS_TOKEN, accessToken)
    } else {
      sessionStorage.removeItem(STORAGE_ACCESS_TOKEN)
    }
  }, [accessToken])

  const login = useCallback(async (returnTo?: string) => {
    const verifier = generateCodeVerifier()
    const challenge = await generateCodeChallenge(verifier)

    sessionStorage.setItem(STORAGE_CODE_VERIFIER, verifier)
    if (returnTo) sessionStorage.setItem(STORAGE_RETURN_TO, returnTo)

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: 'openid email profile',
      code_challenge: challenge,
      code_challenge_method: 'S256'
    })
    window.location.href = `https://${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`
  }, [])

  const logout = useCallback(() => {
    setAccessToken(null)
    setSub(null)
    setEmail(null)
    sessionStorage.removeItem(STORAGE_ACCESS_TOKEN)
    sessionStorage.removeItem(STORAGE_SUB)
    sessionStorage.removeItem(STORAGE_EMAIL)
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      logout_uri: window.location.origin
    })
    window.location.href = `https://${COGNITO_DOMAIN}/logout?${params.toString()}`
  }, [])

  const handleCallback = useCallback(
    async (code: string) => {
      setLoading(true)
      try {
        const verifier = sessionStorage.getItem(STORAGE_CODE_VERIFIER)
        if (!verifier) throw new Error('Missing code verifier')

        const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: CLIENT_ID,
            redirect_uri: REDIRECT_URI,
            code,
            code_verifier: verifier
          }).toString()
        })

        if (!res.ok) throw new Error(`Token exchange failed: ${res.status}`)

        const tokens = (await res.json()) as { access_token: string; id_token: string }

        const idPayload = JSON.parse(atob(tokens.id_token.split('.')[1])) as {
          sub: string
          email?: string
        }

        sessionStorage.setItem(STORAGE_ACCESS_TOKEN, tokens.access_token)
        sessionStorage.setItem(STORAGE_SUB, idPayload.sub)
        if (idPayload.email) sessionStorage.setItem(STORAGE_EMAIL, idPayload.email)

        setAccessToken(tokens.access_token)
        setSub(idPayload.sub)
        setEmail(idPayload.email ?? null)

        sessionStorage.removeItem(STORAGE_CODE_VERIFIER)

        const returnTo = sessionStorage.getItem(STORAGE_RETURN_TO) ?? '/dashboard'
        sessionStorage.removeItem(STORAGE_RETURN_TO)
        window.location.replace(returnTo)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  return (
    <AuthContext.Provider value={{ isAuthenticated: !!accessToken, sub, email, accessToken, loading, login, logout }}>
      {children}
      <AuthCallbackHandler onCode={handleCallback} />
    </AuthContext.Provider>
  )
}

function AuthCallbackHandler({ onCode }: { onCode: (code: string) => Promise<void> }) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (!code || window.location.pathname !== '/auth/callback') return
    void onCode(code)
  }, [onCode])
  return null
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}
